import {inject, Inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {Observable, Subject} from 'rxjs';
import {RxStompConfig} from '@stomp/rx-stomp';
import {Message} from '@stomp/stompjs';
import {Store} from '@ngrx/store';
import * as fromRoot from '../store/app.reducers';
import * as UserActions from '../store/user/user.actions';
import {State} from '../store/user/user.reducers';
import {BidWsUpdateOfferBasedService} from './bid-ws-update-offer-based.service';
import {VehicleWsUpdateService} from './vehicle-ws-update.service';
import {RxStompService} from './rx-stomp.service';
import {ReportingWsUpdateService} from './reporting-ws-update.service';
import {APP_CONFIG} from '../misc/inject-tokens';
import {AppConfig} from '../model/app-config.model';
import {ucsIsNil} from '../misc/utils';
import {Location} from '@angular/common';
import {BarAlertSignalStore} from '../store/alert/bar-alert.signal-store';
import {ToastAlertSignalStore} from '../store/alert/toast-alert.signal-store';

/**
 * This service handles authentication matters
 */
@Injectable()
export class AuthenticationService {

  // the local storage key to the JWT token
  static readonly TOKEN_IDENTIFIER = 'ucs-jwt-token';
  currentConnectionStatus: number;
  wsReconnectSubject = new Subject();
  readonly barAlertStore = inject(BarAlertSignalStore);
  readonly toastAlertStore = inject(ToastAlertSignalStore);

  private stompConfig: RxStompConfig = {
    brokerURL: this.config.stompConfigUrl, // websockets endpoint
    connectHeaders: {
      'Authorization': ''
    },
    heartbeatIncoming: 5000,
    heartbeatOutgoing: 0,
    reconnectDelay: 3000,
    discardWebsocketOnCommFailure: true,
    debug: (msg: string): void => {
      if (this.config.stompConfigDebug) {
        console.log(new Date(), msg);
      }
    }
  };
  private isWebsocketReconnecting = false;

  constructor(private translate: TranslateService, private stompService: RxStompService,
              private bidWsUpdateOfferBasedService: BidWsUpdateOfferBasedService,
              private vehicleWsUpdateService: VehicleWsUpdateService,
              private reportingWsUpdateService: ReportingWsUpdateService,
              private router: Router, private http: HttpClient, private store: Store<fromRoot.AppState>,
              @Inject(APP_CONFIG) private config: AppConfig, private location: Location) {

    // show alert bar when websocket connection to backend is lost
    this.stompService.webSocketErrors$.subscribe(() => {
      if (!this.isWebsocketReconnecting) { // prevents raising multiple alerts for the same issue (still reconnecting)
        this.isWebsocketReconnecting = true;
        const message = this.translate.instant('connection.lost');
        this.barAlertStore.updateAlert(!this.location.path().startsWith('/maintenance'), {
          isDismissible: false,
          type: 'warning',
          message
        });
      }
    });

    // show alert bar when websocket connection is re-established to backend
    this.stompService.connected$.subscribe(() => {
      if (this.isWebsocketReconnecting) {
        this.isWebsocketReconnecting = false;
        const message = this.translate.instant('connection.reconnected');
        this.barAlertStore.updateAlert(!this.location.path().startsWith('/maintenance'), {
          isDismissible: false,
          type: 'warning',
          message
        });
        // request a reload of offers because we may have missed bids in the meantime while trying to reconnect
        this.wsReconnectSubject.next('ws reconnected');
      }
    });
  }

  // static methods here serve as user-based convenience methods to avoid code duplication in components

  /**
   * Checks if current user has permission to buy offers for the given distribution channel.
   *
   * @param {DistributionChannel} channel: The channel for which to check if the current user can buy offers for
   * @param {State} userState: The user state to check if the current user has the necessary permission
   * @returns {boolean} True, if the current user can buy offers for the channel, False otherwise
   */
  static canBuyOffers(channel: DistributionChannel, userState: State): boolean {
    if (userState) {
      switch (channel) {
      case 'PIA':
        return userState.canBuyOffersPia;
      case 'PB':
        return userState.canBuyOffersPb;
      case 'ALL_UC':
        return userState.canBuyOffersAllUc;
      case 'DIN_BIL':
        return userState.canBuyOffersDinBil;
      case 'VGRX':
        return userState.canBuyOffersVgrx;
      default:
        return false;
      }
    } else {
      return false;
    }
  }

  /**
   * Checks if current user has permission to sell offers for the given distribution channel.
   *
   * @param {DistributionChannel} channel: The channel for which to check if the current user can sell offers for
   * @param {State} userState: The user state to check if the current user has the necessary permission
   * @returns {boolean} True, if the current user can sell offers for the channel, False otherwise
   */
  static canSellOffers(channel: DistributionChannel, userState: State): boolean {
    if (userState) {
      switch (channel) {
      case 'PIA':
        return userState.canSellOffersPia;
      case 'PB':
        return userState.canSellOffersPb;
      case 'ALL_UC':
        return userState.canSellOffersAllUc;
      case 'DIN_BIL':
        return userState.canSellOffersDinBil;
      case 'VGRX':
        return userState.canSellOffersVgrx;
      default:
        return false;
      }
    } else {
      return false;
    }
  }

  /**
   * Checks if current user has permission to sell offers for any distribution channel
   *
   * @param {State} userState: The user state to check if the current user has the necessary permission
   * @returns {boolean} True, if the current user can sell offers for any channel, False otherwise
   */
  static canSellOffersForAnyChannel(userState: State): boolean {
    return userState && (userState.canSellOffersPia || userState.canSellOffersPb || userState.canSellOffersAllUc ||
        userState.canSellOffersDinBil || userState.canSellOffersVgrx);
  }

  /**
   * Checks if current user has permission to view offers in offer maintenance for the given distribution channel.
   *
   * @param {DistributionChannel} channel: The channel for which to check if the current user can view offers for
   * @param {State} userState: The user state to check if the current user has the necessary permission
   * @returns {boolean} True, if the current user can view maintenance offers for the channel, False otherwise
   */
  static canViewOffersForMaintenance(channel: DistributionChannel, userState: State): boolean {
    return (this.canSellOffers(channel, userState) || (channel === 'PIA' && userState.isExaminerPia)
        || (channel === 'PB' && (userState.isClerkPb || userState.isCustodianPb)));
  }

  /**
   * Checks if current user has permission to view vehicles for the given distribution channel.
   *
   * @param {DistributionChannel} channel: the channel to check the role for
   * @param {State} userState: the user state to check if the current user has the necessary permission
   * @returns whether the user may view vehicles of the passed channel
   */
  static canViewVehicles(channel: DistributionChannel, userState: State): boolean {
    return userState.isDevPoi || userState.isSupportPoi ||
        (channel === 'PIA' &&
            (userState.isBuyNowSellerPia || userState.isAuctionSellerPia || userState.isAdminPia ||
                userState.isExaminerPia || userState.isBuyerEnforcedAuction || userState.isViewerEnforcedAuction)) ||
        (channel === 'PB' &&
            (userState.isBuyNowSellerPb || userState.isAuctionSellerPb || userState.isCustodianPb ||
                userState.isAdminPb || userState.isSupportPb || userState.isSurveyorPb ||
                userState.isClerkPb || userState.isClerkBmiPb)) ||
        (channel === 'ALL_UC' &&
            (userState.isBuyNowSellerAllUc || userState.isAuctionSellerAllUc || userState.isAdminAllUc)) ||
        (channel === 'DIN_BIL' &&
            (userState.isBuyNowSellerDinBil || userState.isAuctionSellerDinBil || userState.isAdminDinBil)) ||
        (channel === 'VGRX' &&
            (userState.isBuyNowSellerVgrx || userState.isAuctionSellerVgrx || userState.isAdminVgrx));
  }

  /**
   * Gets the first permitted channel that the user has permission to buy offers for.
   *
   * @param {State} userState: The user state to access the user's permissions for evaluation
   * @returns {DistributionChannel} the first permitted channel that the user has permission to buy offers for
   */
  static getPermittedChannelToBuyOffers(userState: State): DistributionChannel {
    if (this.canBuyOffers(<DistributionChannel>'ALL_UC', userState)) {
      return <DistributionChannel>'ALL_UC';
    }

    if (this.canBuyOffers(<DistributionChannel>'PB', userState)) {
      return <DistributionChannel>'PB';
    }

    if (this.canBuyOffers(<DistributionChannel>'PIA', userState)) {
      return <DistributionChannel>'PIA';
    }

    if (this.canBuyOffers(<DistributionChannel>'DIN_BIL', userState)) {
      return <DistributionChannel>'DIN_BIL';
    }

    if (this.canBuyOffers(<DistributionChannel>'VGRX', userState)) {
      return <DistributionChannel>'VGRX';
    }

    console.error('could not get any permitted channel for user to buy offers for');
  }

  /**
   * Gets the permitted channels the user has permission to buy offers for.
   *
   * @param {State} userState: The user state to access the user's permissions for evaluation
   * @returns {DistributionChannel[]} an array of all permitted channels the user has permission to buy offers for
   */
  static getPermittedChannelsToBuyOffers(userState: State): DistributionChannel[] {
    const permitted = [];

    if (this.canBuyOffers(<DistributionChannel>'ALL_UC', userState)) {
      permitted.push('ALL_UC');
    }

    if (this.canBuyOffers(<DistributionChannel>'PB', userState)) {
      permitted.push('PB');
    }

    if (this.canBuyOffers(<DistributionChannel>'PIA', userState)) {
      permitted.push('PIA');
    }

    if (this.canBuyOffers(<DistributionChannel>'DIN_BIL', userState)) {
      permitted.push('DIN_BIL');
    }

    if (this.canBuyOffers(<DistributionChannel>'VGRX', userState)) {
      permitted.push('VGRX');
    }

    return permitted;
  }

  /**
   * Gets the first permitted channel that the user has permission to view offers for maintenance.
   *
   * @param {State} userState: The user state to access the user's permissions for evaluation
   * @returns {DistributionChannel} the first permitted channel that the user has permission to view maintenance offers
   */
  static getPermittedChannelToViewOffersForMaintenance(userState: State): DistributionChannel {
    if (this.canViewOffersForMaintenance(<DistributionChannel>'PIA', userState)) {
      return <DistributionChannel>'PIA';
    }

    if (this.canViewOffersForMaintenance(<DistributionChannel>'PB', userState)) {
      return <DistributionChannel>'PB';
    }

    if (this.canViewOffersForMaintenance(<DistributionChannel>'ALL_UC', userState)) {
      return <DistributionChannel>'ALL_UC';
    }

    if (this.canViewOffersForMaintenance(<DistributionChannel>'DIN_BIL', userState)) {
      return <DistributionChannel>'DIN_BIL';
    }

    if (this.canViewOffersForMaintenance(<DistributionChannel>'VGRX', userState)) {
      return <DistributionChannel>'VGRX';
    }

    console.error('could not get any permitted channel for user to sell offers for');
  }

  /**
   * Gets the first permitted channel that the user has permission to view vehicles for.
   *
   * @param {State} userState: The user state to access the user's permissions for evaluation
   * @returns {DistributionChannel} the first permitted channel that the user has permission to view vehicles for
   */
  static getPermittedChannelToViewVehicles(userState: State): DistributionChannel {
    if (this.canViewVehicles(<DistributionChannel>'ALL_UC', userState)) {
      return <DistributionChannel>'ALL_UC';
    }

    if (this.canViewVehicles(<DistributionChannel>'PB', userState)) {
      return <DistributionChannel>'PB';
    }

    if (this.canViewVehicles(<DistributionChannel>'PIA', userState)) {
      return <DistributionChannel>'PIA';
    }

    if (this.canViewVehicles(<DistributionChannel>'DIN_BIL', userState)) {
      return <DistributionChannel>'DIN_BIL';
    }

    if (this.canViewVehicles(<DistributionChannel>'VGRX', userState)) {
      return <DistributionChannel>'VGRX';
    }

    console.error('could not get any permitted channel for user to view vehicles for');
  }

  /**
   * Check if current user is the seller of the given offer
   * @param {OfferDto} offer The offer for which to check if the current user is the seller
   * @param {UserInfoDto} userInfo The userInfo to check if the given user is the seller
   * @returns {boolean} True, if the current user is the seller of the given offer, False otherwise
   */
  static isOfferOfCurrentUser(offer: OfferDto, userInfo: UserInfoDto): boolean {
    return userInfo.dealers.some(dealer => dealer.id === offer.sellerInformation.id);
  }

  /**
   * Check if current user is the followupProposal dealer
   * @param {ProposalDto} followupProposal From this offer
   * @param {UserInfoDto} userInfo The userInfo to check if the given user is the followupProposal dealer
   * @returns {boolean} True, if the current user is the followupProposal dealer, False otherwise
   */
  static isFollowupProposalOfCurrentUser(followupProposal: ProposalDto, userInfo: UserInfoDto): boolean {
    if (ucsIsNil(followupProposal)) {
      return false;
    } else if (followupProposal.dealer.id === userInfo.currentDealerId) {
      return true;
    }
    return false;
  }

  /**
   * Check if the current user is admin for a given channel
   * @param {State} userState The user data
   * @param {DistributionChannel} channel The channel to check for
   * @returns {boolean} True, if the current user is admin for the given channel, False otherwise
   */
  static isAdminForChannel(channel: DistributionChannel, userState: State): boolean {
    return (userState.isAdminAllUc && channel === 'ALL_UC') ||
        (userState.isAdminPb && channel === 'PB') ||
        (userState.isAdminPia && channel === 'PIA') ||
        (userState.isAdminDinBil && channel === 'DIN_BIL') ||
        (userState.isAdminVgrx && channel === 'VGRX');
  }

  /**
   * Check if the current user is admin for any channel (ALL_UC, PB, PIA, DIN_BIL)
   * @param {State} userState The user data
   * @returns {boolean} True, if the current user is admin for any channel, False otherwise
   */
  static isAdminForAnyChannel(userState: State): boolean {
    return (userState.isAdminAllUc || userState.isAdminPb || userState.isAdminPia || userState.isAdminDinBil);
  }

  /**
   * Check if the current user is POI support or POI dev
   * @param {State} userState The user data
   * @returns {boolean} True, if the current user is POI support or POI dev, False otherwise
   */
  static isSupportOrDevPoi(userState: State): boolean {
    return (userState.isSupportPoi || userState.isDevPoi);
  }

  /**
   * Check if the current user is POI dev
   * @param {State} userState The user data
   * @returns {boolean} True, if the current user is POI dev, False otherwise
   */
  static isDevPoi(userState: State): boolean {
    return userState.isDevPoi;
  }

  /**
   * Checks whether the user is allowed to create an offer of the given type for the given channel.
   * @param type The type of offer to create.
   * @param channel The channel for which to create the offer.
   * @param userState The user state.
   */
  static isUserAllowedToCreateOfferTypeForChannel(type: OfferType, channel: DistributionChannel, userState: State): boolean {
    if (type === 'BUYNOW') {
      return this.isSupportOrDevPoi(userState) || this.isAdminForChannel(channel, userState) ||
          this.isBuyNowSellerForChannel(channel, userState);
    } else if (type === 'AUCTION') {
      return this.isSupportOrDevPoi(userState) || this.isAdminForChannel(channel, userState) ||
          this.isAuctionSellerForChannel(channel, userState);
    }
    return false;
  }

  static isBuyNowSellerForChannel(channel: DistributionChannel, userState: State) {
    return (channel === 'PIA' && userState.isBuyNowSellerPia) ||
        (channel === 'PB' && userState.isBuyNowSellerPb) ||
        (channel === 'ALL_UC' && userState.isBuyNowSellerAllUc) ||
        (channel === 'DIN_BIL' && userState.isBuyNowSellerDinBil) ||
        (channel === 'VGRX' && userState.isBuyNowSellerVgrx);
  }

  static isAuctionSellerForChannel(channel: DistributionChannel, userState: State) {
    return (channel === 'PIA' && userState.isAuctionSellerPia) ||
        (channel === 'PB' && userState.isAuctionSellerPb) ||
        (channel === 'ALL_UC' && userState.isAuctionSellerAllUc) ||
        (channel === 'DIN_BIL' && userState.isAuctionSellerDinBil) ||
        (channel === 'VGRX' && userState.isAuctionSellerVgrx);
  }

  /**
   * Checks whether a user is only allowed to see the finished offers, regarding its current dealer selection
   * If false there is no restriction of this kind.
   *
   * @param userInfo the current user state from the state
   */
  static isOnlyAllowedToSeeFinishedOffersReasonLocked(userInfo: UserInfoDto): boolean {
    if (userInfo && userInfo.roles && userInfo.roles.entries()) {
      return userInfo.roles.includes(<Role>'LOCKED');
    }
    return false;
  }

  /**
   * Checks whether a user is only allowed to see the finished offers, regarding its current dealer selection
   * If false there is no restriction of this kind.
   *
   * @param userInfo the current user state from the state
   */
  static isOnlyAllowedToSeeFinishedOffersReasonGtc(userInfo: UserInfoDto): boolean {
    if (userInfo && userInfo.missingGtcAcceptance) {
      return true;
    }
    return false;
  }

  /**
   * Checks whether a user is allowed to refresh the vehicle data in an already existing offer.
   *
   * @param userState The user state object
   */
  static isAllowedToRefreshVehicleDataInOffer(userState: State): boolean {
    if (userState && (
      this.isSupportOrDevPoi(userState) ||
        userState.isSupportPb ||
        this.isAdminForAnyChannel(userState) ||
        this.canSellOffers('PB', userState) ||
        this.canSellOffers('ALL_UC', userState) ||
        this.canSellOffers('DIN_BIL', userState) ||
        this.canSellOffers('VGRX', userState) ||
        userState.isBuyNowSellerPia
    )) {
      return true;
    }
    return false;
  }

  /**
   * Returns true, if the user is logged in (a JWT token is present in the local storage.
   * False otherwise.
   * This method gives no indication whether the authentication ist still valid to use the UCS sales API,
   * it may also be expired.
   *
   * @returns {boolean} Whether the user is logged in (a JWT token exists)
   */
  isAuthenticated(): boolean {
    if (this.supportsLocalStorage()) {
      const token = localStorage.getItem(AuthenticationService.TOKEN_IDENTIFIER);
      if (token !== null && token !== undefined) {
        return true;
      }
    } else {
      this.toastAlertStore.danger(this.translate.instant('error.localstorage-not-supported'));
    }
    return false;
  }

  /**
   * Clears the authentication token from the local storage
   */
  clearToken(): void {
    localStorage.removeItem(AuthenticationService.TOKEN_IDENTIFIER);
  }

  /**
   * Logs the user out by removing the JWT token.
   * No redirection to logout page needed since the logout method is called
   * when the logout component is initialized i.e. a routing to /logout is performed
   */
  logout(): void {
    if (this.supportsLocalStorage()) {
      this.clearToken();
    } else {
      this.toastAlertStore.danger(this.translate.instant('error.localstorage-not-supported'));
    }

    if (this.stompService.connected()) {
      this.disconnectWebsockets();
    }
  }

  /**
   * Store the JWT authentication token.
   *
   * @param token the token in String representation
   */
  storeToken(token: string): void {
    localStorage.setItem(AuthenticationService.TOKEN_IDENTIFIER, token);
    this.store.dispatch(new UserActions.UpdateUserInfoAction());
  }

  /**
   * Returns the data of the currently authenticated user.
   */
  getUserData(): Observable<UserInfoDto> {
    return this.http.get<UserInfoDto>(this.config.authenticationUrl + '/authentication/whoami');
  }

  /**
   * Set the current login dealer to the dealer with the given ID.
   * @param {string} dealerId The ID of the dealer to set.
   */
  setDealer(dealerId: string): Observable<string> {
    this.disconnectWebsockets();
    return this.http.put(this.config.authenticationUrl + '/authentication/currentdealer', dealerId, {responseType: 'text'});
  }

  /**
   * Returns the JWT authentication token. Can be null if not authenticated.
   */
  getToken(): string {
    if (this.supportsLocalStorage()) {
      return localStorage.getItem(AuthenticationService.TOKEN_IDENTIFIER);
    }
    return null;
  }

  /**
   * Check if the current device supports local storage.
   * @returns {boolean} If the current device supports local storage.
   */
  supportsLocalStorage(): boolean {
    try {
      return 'localStorage' in window && window['localStorage'] !== null;
    } catch (e) {
      console.debug(e);
      return false;
    }
  }

  /**
   * Connect to websockets endpoint.
   */
  connectWebsockets(): void {
    if (!this.stompService.connected()) {
      this.stompConfig.connectHeaders['Authorization'] = this.getToken();
      this.stompService.configure(this.stompConfig);
      this.stompService.stompErrors$.subscribe(this.ws_error_handler);
      this.stompService.activate();

      this.stompService.connectionState$.subscribe(connectionState => {
        if (!ucsIsNil(this.currentConnectionStatus)
            && this.currentConnectionStatus === 3 && connectionState === 0
            && !this.isWebsocketReconnecting) {
          const message = this.translate.instant('connection.temporally-lost');
          this.barAlertStore.updateAlert(!this.location.path().startsWith('/maintenance'), {
            isDismissible: false,
            type: 'warning',
            message
          });
        }
        this.currentConnectionStatus = connectionState;
      });
    }
  }

  /**
   * Disconnect from websockets endpoint and unsubscribe beforehand.
   */
  disconnectWebsockets(): void {
    if (this.stompService.connected()) {
      this.bidWsUpdateOfferBasedService.unsubscribeBidStatusUpdateTopic();
      this.vehicleWsUpdateService.unsubscribeVehicleUpdateTopic();
      this.reportingWsUpdateService.unsubscribeReportingTopic();
      this.stompService.deactivate();
    }
  }

  login(target: string) {
    window.location.href = this.config.authenticationUrl + '/authentication/logon?target=' + target;
  }

  private ws_error_handler = (message: Message) => {
    // wildcard handle all custom authorization/authentication errors - disconnect websocket
    const permissionErrorRegex: RegExp = /error.(authorization|authentication).*/;
    const errorResponse: BasicWSErrorResponse = JSON.parse(message.body);
    console.error('Server returned an error. error-reference: %s. Message: %s. code: %s',
      errorResponse.reference, errorResponse.message, errorResponse.code);
    if (permissionErrorRegex.test(errorResponse.code)) {
      const currentToken = this.getToken();
      if (currentToken && !this.stompConfig.connectHeaders['Authorization'].includes(currentToken)) {
        this.connectWebsockets();
      } else {
        this.stompService.deactivate();
        this.router.navigateByUrl('logout');
      }
    }
  };

}
